import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin, Star, Truck } from '../../lib/lucide-proxy';
import { supabase } from '../../config/supabase';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const AllBreweries = () => {
  const [breweries, setBreweries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchBreweries();
  }, []);

  const fetchBreweries = async () => {
    try {
      // Fetching ALL active breweries, not just featured
      const { data, error } = await supabase
        .from('breweries')
        .select(`
          *,
          reviews(rating)
        `)
        .eq('is_active', true);

      if (error) throw error;

      const formatted = data.map(b => ({
        ...b,
        rating: b.reviews?.length ? b.reviews.reduce((acc, curr) => acc + curr.rating, 0) / b.reviews.length : 0,
        reviewCount: b.reviews?.length || 0
      }));

      setBreweries(formatted);
    } catch (error) {
      console.error('Error fetching breweries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = breweries.filter(b => 
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    b.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Todas as Cervejarias - Cerveja Delivery</title>
      </Helmet>
      <div className="bg-cream-300 min-h-screen pt-12 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <h1 className="font-display font-bold text-4xl text-dark-900 mb-6 text-center">
            Cervejarias Parceiras
          </h1>
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-dark-500 w-5 h-5" />
            <input 
              type="text" 
              placeholder="Buscar por nome ou cidade..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-dark-900 placeholder-dark-500 focus:ring-2 focus:ring-primary-500 focus:outline-none shadow-lg"
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-dark-500 py-12">Nenhuma cervejaria encontrada.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((brewery) => (
                <Link to={`/loja/${brewery.slug}`} key={brewery.id} className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all group">
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={brewery.cover_image || 'https://images.unsplash.com/photo-1571613316887-6f8d5cbf7ef7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'} 
                      alt={brewery.name} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" 
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-2 py-1 rounded-lg text-xs font-bold flex items-center">
                      <Star className="w-3 h-3 text-yellow-500 fill-current mr-1" /> {brewery.rating.toFixed(1)}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <img src={brewery.logo} className="w-10 h-10 rounded-lg object-cover border border-gray-100 mr-3" alt="logo" />
                      <div>
                        <h3 className="font-bold text-dark-900 leading-tight">{brewery.name}</h3>
                        <div className="flex items-center text-xs text-dark-500">
                          <MapPin className="w-3 h-3 mr-1" />
                          <span className="truncate max-w-[150px]">{brewery.address}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-dark-600 line-clamp-2 mb-4">{brewery.description}</p>
                    <div className="flex items-center text-xs text-dark-500 bg-gray-50 p-2 rounded-lg">
                      <Truck className="w-3 h-3 mr-2" />
                      {brewery.delivery_config?.type === 'none' ? 'Apenas Menu' : 'Delivery Disponível'}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AllBreweries;